import React, { useEffect, useState } from 'react'
import { scaleBand, scaleLinear } from '@visx/scale'
import { Bar } from '@visx/shape'
import { Group } from '@visx/group'
import { AxisBottom } from '@visx/axis'
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip'
import { ChartDatum } from './types'

type StagesBarChartProps = {
    width: number
    height: number
    data: ChartDatum[]
}

const useMediaQuery = (query: string): boolean => {
    const [matches, setMatches] = useState(() => {
        if (typeof window === 'undefined') return false

        return window.matchMedia(query).matches
    })

    useEffect(() => {
        if (typeof window === 'undefined') return undefined

        const mediaQuery = window.matchMedia(query)
        const update = (): void => setMatches(mediaQuery.matches)

        update()
        mediaQuery.addEventListener('change', update)

        return () => mediaQuery.removeEventListener('change', update)
    }, [query])

    return matches
}

const BREAKPOINTS = {
    mobile: '(max-width: 767px)',
    small: '(min-width: 993px) and (max-width: 1199px)',
    medium: '(min-width: 1200px) and (max-width: 1600px)'
} as const

const StagesBarChart: React.FC<StagesBarChartProps> = ({ width, height, data }) => {
    const topPadding = 0
    const chartYOffset = 0
    const axisColor = 'var(--mantine-text-default)'
    const textColor = 'var(--mantine-text-default)'
    const axisLabelFontSize = '1.2rem'

    const isMobileViewport = useMediaQuery(BREAKPOINTS.mobile)
    const isSmallViewport = useMediaQuery(BREAKPOINTS.small)
    const isMediumViewport = useMediaQuery(BREAKPOINTS.medium)

    const tickAngle = isMediumViewport ? -45 : isSmallViewport ? -25 : 0
    const bottomPadding = height < 100 ? 18 : isMediumViewport ? 48 : 24
    const plotBottom = Math.max(topPadding + 1, height - bottomPadding - chartYOffset)

    const xDomain = data.map(d => d.label)
    const maxValue = Math.max(...data.map(d => d.value), 0)
    const yDomainMax = maxValue > 0 ? maxValue : 1

    const xScale = scaleBand<string>({
        domain: xDomain,
        range: [0, width],
        padding: 0.3
    })

    const yScale = scaleLinear<number>({
        domain: [0, yDomainMax],
        range: [plotBottom, topPadding]
    })

    const { tooltipData, tooltipLeft = 0, tooltipTop = 0, tooltipOpen, showTooltip, hideTooltip } = useTooltip<
        ChartDatum
    >()

    const { containerRef, TooltipInPortal } = useTooltipInPortal({
        detectBounds: true,
        scroll: true
    })

    const chartTranslateY = isSmallViewport ? -12 : 0

    return (
        <div style={{ position: 'relative', overflow: 'visible' }}>
            <svg
                ref={containerRef}
                width={width}
                height={height}
                style={{
                    display: 'block',
                    overflow: 'visible',
                    transform: chartTranslateY !== 0 ? `translateY(${chartTranslateY}px)` : undefined
                }}
            >
                <Group>
                    {data.map(d => {
                        const barWidth = xScale.bandwidth()
                        const x = xScale(d.label) ?? 0
                        const y = yScale(d.value)
                        const barTopY = d.value > 0 ? y : plotBottom
                        const barHeight = plotBottom - y

                        const handleShowTooltip = (): void => {
                            showTooltip({
                                tooltipData: d,
                                tooltipLeft: Math.min(width - 8, Math.max(8, x + barWidth / 2)),
                                tooltipTop: Math.min(plotBottom - 8, Math.max(topPadding, barTopY))
                            })
                        }

                        const mouseHandlers = isMobileViewport
                            ? {}
                            : {
                                  onMouseEnter: handleShowTooltip,
                                  onMouseMove: handleShowTooltip,
                                  onMouseLeave: hideTooltip
                              }

                        return (
                            <Group key={d.date}>
                                {d.value > 0 && <Bar x={x} y={y} width={barWidth} height={barHeight} fill="#228be6" />}
                                <Bar
                                    x={x}
                                    y={0}
                                    width={barWidth}
                                    height={plotBottom}
                                    fill="transparent"
                                    {...mouseHandlers}
                                />
                            </Group>
                        )
                    })}

                    <AxisBottom
                        top={plotBottom}
                        scale={xScale}
                        tickLabelProps={() => ({
                            fontSize: axisLabelFontSize,
                            fill: axisColor,
                            textAnchor: tickAngle !== 0 ? 'end' : 'middle',
                            angle: tickAngle
                        })}
                        stroke={axisColor}
                        tickStroke={axisColor}
                    />
                </Group>
            </svg>

            {isMobileViewport && (
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    {data.map(d => {
                        if (d.value <= 0) return null

                        const barWidth = xScale.bandwidth()
                        const x = xScale(d.label) ?? 0
                        const labelTop = Math.max(topPadding, yScale(d.value))

                        return (
                            <span
                                key={`label-${d.date}`}
                                style={{
                                    position: 'absolute',
                                    left: x + barWidth / 2,
                                    top: labelTop,
                                    transform: 'translate(-50%, calc(-100% - 4px))',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: textColor,
                                    lineHeight: 1
                                }}
                            >
                                {d.value}
                            </span>
                        )
                    })}
                </div>
            )}

            {!isMobileViewport && tooltipOpen && tooltipData && (
                <TooltipInPortal
                    className="visx-tooltip"
                    key={tooltipData.date}
                    detectBounds={false}
                    offsetLeft={0}
                    offsetTop={0}
                    top={tooltipTop}
                    left={tooltipLeft}
                    style={{
                        ...defaultStyles,
                        borderRadius: 4,
                        padding: '6px 8px',
                        transform: 'translate(-50%, calc(-100% - 12px))',
                        zIndex: 2147483647,
                        pointerEvents: 'none',
                        fontSize: '1.2rem'
                    }}
                >
                    {tooltipData.value} {tooltipData.value === 1 ? 'stage' : 'stages'}
                </TooltipInPortal>
            )}
        </div>
    )
}

export default StagesBarChart
